<template>
  <hb-basic-page
    :action-buttons="$vuetify.breakpoint.smAndDown ? {} : {}"
    :back-button="{ show: true, fallback: 'tenant_start' }"
    :extension-height="$vuetify.breakpoint.xsOnly ? 112 : 120"
    :fill-height="surveys.length === 0 || !managementSurveyHasAnswers || !personnelSurveyHasAnswers"
    :loading="loading"
    :subtle-loading="subtleLoading"
    :title="$tc('reports.title', 2)"
    name="reports-view"
    @handle-search="handleSearch"
  >
    <template v-if="showSurveySelectionsInAppBar" v-slot:app-bar-extension>
      <v-row no-gutters>
        <v-col cols="12" lg="10" md="12" offset-lg="1" offset-xl="2" xl="8">
          <surveys-selector :app-bar="true" :management-surveys="managementSurveys"
                            :personnel-surveys="personnelSurveys"/>
        </v-col>
      </v-row>
    </template>

    <v-col v-if="showReports" v-scroll="handleScroll" cols="12">
      <v-row no-gutters>
        <v-col cols="12" lg="10" md="12" offset-lg="1" offset-xl="2" xl="8">
          <v-card color="white" light>
            <v-card-title>
              {{ $t('reports.surveys_used_for_reports') }}
            </v-card-title>
            <v-card-text>
              <div
                v-if="!loading && !showNoContentView && (selectedPersonnelSurveyIsOngoing || selectedManagementSurveyIsOngoing)"
                class="pb-5 text-body-2 text-sm-body-1">
                {{ $t('reports.ongoing_analysis_selected') }}
              </div>
              <div v-else class="pb-5 text-body-2 text-sm-body-1">
                {{ $t('reports.you_can_choose_which_analysis_are_used') }}
              </div>
              <div ref="surveySelectionEndMarker"></div>
              <v-container class="px-0" fluid>
                <surveys-selector :management-surveys="managementSurveys" :personnel-surveys="personnelSurveys"/>

                <v-alert
                  v-if="(!selectedManagementSurvey || (!$isPaidTenant && selectedManagementSurveyShares.length === 0)) && (!selectedPersonnelSurvey || (!$isPaidTenant && selectedPersonnelSurveyShares.length === 0))"
                  class="mt-8 mb-0" prominent text
                  type="info">
                  <span v-if="!$isPaidTenant">{{
                      $t('reports.you_must_choose_atleast_one_analysis_and_target_group')
                    }}</span>
                  <span v-if="$isPaidTenant">{{ $t('reports.you_must_choose_atleast_one_analysis') }}</span>
                </v-alert>
              </v-container>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <template
        v-if="(selectedManagementSurvey && ($isPaidTenant || selectedManagementSurveyShares.length > 0)) || (selectedPersonnelSurvey && ($isPaidTenant || selectedPersonnelSurveyShares.length > 0))">
        <v-row v-if="selectedPersonnelSurvey && reports.filter(r => r.type === 'PERSONNEL')">
          <v-col cols="12" sm="10" offset-sm="1" md="8" offset-md="2" lg="6" offset-lg="3" xl="4" offset-xl="4">
            <company-fitness-index-chart :calculation="reports.filter(r => r.type === 'PERSONNEL')[0].calculation" />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" lg="10" md="12" offset-lg="1" offset-xl="2" xl="8">
            <v-row justify="center">
              <report-category-card :surveys="reports" category="efficiency"/>
              <report-category-card :surveys="reports" category="capability"/>
              <report-category-card
                :management-survey-has-answers="managementSurveyHasAnswers"
                :personnel-survey-has-answers="personnelSurveyHasAnswers"
                :surveys="reports"
                category="leadership"
              />
              <!--<report-category-card :surveys="surveys" category="culture_leadership"/>-->
              <report-category-card v-if="managementSurveyHasAnswers" :surveys="reports" category="economy_customers"/>
            </v-row>
          </v-col>
        </v-row>
        <v-row class="grey lighten-4 mt-15 pt-6 pb-6">
          <v-col cols="12" lg="10" md="12" offset-lg="1" offset-xl="2" xl="8">
            <v-row>
              <v-col cols="12">
                <report-culture-card :surveys="reports" category="culture"/>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row class="pt-3 pb-10">
          <v-col cols="12" lg="10" md="12" offset-lg="1" offset-xl="2" xl="8">
            <v-row>
              <v-col ref="inputMap" cols="12">
                <report-input-map-card :management-survey="managementSurvey" :personnel-survey="personnelSurvey"/>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </template>
    </v-col>

    <template v-if="showNoContentView">
      <v-col class="pa-0 fill-height" lg="8" md="10" offset-lg="2" offset-md="1" offset-xl="3" xl="6">
        <v-row align="center" class="fill-height" no-gutters>
          <no-content-view
            :description="
              $hasMatchingTenantRoles(['tenant_admin'])
                ? $t('reports.surveys_have_no_answers_text')
                : $t('reports.surveys_have_no_answers_text_basic_user')
            "
            :function-button="$hasMatchingTenantRoles(['tenant_admin'])"
            :function-button-text="$t('reports.go_to_survey')"
            :prop-function="goToSurveys"
            :reload-button="false"
            :title="
              !managementSurveyHasAnswers && !personnelSurveyHasAnswers
                ? $t('reports.surveys_have_no_answers')
                : !managementSurveyHasAnswers
                ? $t('reports.management_survey_has_no_answers')
                : $t('reports.personnel_survey_has_no_answers')
            "
            function-button-icon="mdi mdi-arrow-right"
            title-image="img/undraw_data_re_80ws.svg"
          />
        </v-row>
      </v-col>
    </template>

    <template v-if="!loading && surveys.length === 0">
      <template v-if="$hasMatchingTenantRoles(['tenant_admin'])">
        <v-col class="pa-0 fill-height" lg="8" md="10" offset-lg="2" offset-md="1" offset-xl="3" xl="6">
          <v-row align="center" class="fill-height" no-gutters>
            <no-content-view
              :description="$t('reports.create_first_survey_help')"
              :description-extra="$t('reports.after_survey_is_created_help')"
              :function-button="true"
              :function-button-text="$t('reports.go_to_survey')"
              :prop-function="goToSurveys"
              :reload-button="false"
              :title="$t('reports.no_reports')"
              function-button-icon="mdi mdi-arrow-right"
              title-image="img/undraw_data_re_80ws.svg"
            />
          </v-row>
        </v-col>
      </template>
      <template v-else>
        <v-col class="pa-0 fill-height" lg="8" md="10" offset-lg="2" offset-md="1" offset-xl="3" xl="6">
          <v-row align="center" class="fill-height" no-gutters>
            <no-content-view
              :description="$t('reports.create_first_survey_help_basic')"
              :description-extra="$t('reports.after_survey_is_created_help')"
              :reload-button="false"
              :title="$t('reports.no_reports')"
              function-button-icon="mdi mdi-arrow-right"
              title-image="img/undraw_data_re_80ws.svg"
            />
          </v-row>
        </v-col>
      </template>
    </template>
  </hb-basic-page>
</template>

<script>
import NoContentView from '@/components/NoContentView';
import surveyApi from '@/api/survey';
import ReportCategoryCard from '@/components/Tenant/Reports/ReportCategoryCard';
import ReportCultureCard from '@/components/Tenant/Reports/ReportCultureCard';
import ReportInputMapCard from '@/components/Tenant/Reports/ReportInputMapCard';
import dayjs from 'dayjs';
import SurveysSelector from '@/components/Tenant/Reports/SurveysSelector';
import isEqual from "lodash/isEqual";
import CompanyFitnessIndexChart from "@/components/Tenant/Dashboard/CompanyFitnessIndexChart";

export default {
  name: 'ReportsView',
  components: {
    CompanyFitnessIndexChart,
    SurveysSelector, ReportInputMapCard, ReportCultureCard, ReportCategoryCard, NoContentView},
  data() {
    return {
      loading: true,
      subtleLoading: false,
      searchPhrase: '',
      surveys: [],
      reports: [],
      showSurveySelectionsInAppBar: false,
    };
  },
  computed: {
    showReports() {
      if (this.loading) return false;
      if (this.surveys.length > 0) {
        if (this.managementSurveys.length > 0) return true;
        if (this.personnelSurveys.length > 0) return true;
      }
      return false;
    },
    showNoContentView() {
      if (this.loading) return false;
      if (this.surveys.length > 0) {
        if (this.managementSurveys.length > 0) return false;
        return this.personnelSurveys.length <= 0;
      }
      return false;
    },
    personnelSurvey() {
      return this.reports?.find(s => s.type === 'PERSONNEL');
    },
    managementSurvey() {
      return this.reports?.find(s => s.type === 'MANAGEMENT');
    },
    personnelSurveys() {
      const today = dayjs();
      const personnelSurveysWithAnswers = this.surveys.filter(s => s.surveyTemplate.type === 'PERSONNEL' && s.latestAnswerAt);
      if (personnelSurveysWithAnswers.length > 0) {
        const endedPersonnelSurveys = personnelSurveysWithAnswers.filter(s => dayjs(s.end).isBefore(today));
        if (endedPersonnelSurveys.length > 0) return endedPersonnelSurveys;
        return [personnelSurveysWithAnswers[0]];
      }
      return [];
    },
    managementSurveys() {
      const today = dayjs();
      const managementSurveysWithAnswers = this.surveys.filter(s => s.surveyTemplate.type === 'MANAGEMENT' && s.latestAnswerAt);
      if (managementSurveysWithAnswers.length > 0) {
        const endedManagementSurveys = managementSurveysWithAnswers.filter(s => dayjs(s.end).isBefore(today));
        if (endedManagementSurveys.length > 0) return endedManagementSurveys;
        return [managementSurveysWithAnswers[0]];
      }
      return [];
    },
    personnelSurveyHasAnswers() {
      return this.personnelSurvey?.calculation?.engineResponse?.metadata?.answers > 0;
    },
    managementSurveyHasAnswers() {
      return this.managementSurvey?.calculation?.engineResponse?.metadata?.answers > 0;
    },
    selectedPersonnelSurvey: {
      get() {
        return this.$store.state.selectedPersonnelSurvey;
      },
      set(value) {
        this.$store.commit('setSelectedPersonnelSurvey', value);
      },
    },
    selectedManagementSurvey: {
      get() {
        return this.$store.state.selectedManagementSurvey;
      },
      set(value) {
        this.$store.commit('setSelectedManagementSurvey', value);
      },
    },
    selectedManagementSurveyShares() {
      return [...this.$store.state.reportFilters.selectedManagementSurveyShares]; // Vaatii kopion jotta muutokset triggeröivät watcherin oikein
    },
    selectedPersonnelSurveyShares() {
      return [...this.$store.state.reportFilters.selectedPersonnelSurveyShares]; // Vaatii kopion jotta muutokset triggeröivät watcherin oikein
    },
    personnelSurveyShares() {
      return [...this.$store.state.reportFilters.personnelSurveyShares];
    },
    managementSurveyShares() {
      return [...this.$store.state.reportFilters.managementSurveyShares];
    },
    selectedPersonnelSurveyIsOngoing() {
      const s = this.reports?.find((su) => su.type === 'PERSONNEL')
      if (s !== undefined && (s.end === undefined || dayjs().isBefore(s.end))) {
        return true;
      }
      return false;
    },
    selectedManagementSurveyIsOngoing() {
      const s = this.reports?.find((su) => su.type === 'MANAGEMENT')
      if (s !== undefined && (s.end === undefined || dayjs().isBefore(s.end))) {
        return true;
      }
      return false;
    },
  },
  watch: {
    selectedPersonnelSurvey(to, from) {
      if (isEqual(to, from)) return;
      if (!this.loading) this.reloadReports();
    },
    selectedManagementSurvey(to, from) {
      if (isEqual(to, from)) return;
      if (!this.loading) this.reloadReports();
    },
    selectedManagementSurveyShares(to, from) {
      if (isEqual(to, from)) return;
      if (!this.loading) this.reloadReports();
    },
    selectedPersonnelSurveyShares(to, from) {
      if (isEqual(to, from)) return;
      if (!this.loading) this.reloadReports();
    },
  },
  methods: {
    handleScroll() {
      if (this.$refs.surveySelectionEndMarker && this.$vuetify.breakpoint.smAndUp) {
        this.showSurveySelectionsInAppBar = this.$refs.surveySelectionEndMarker.getBoundingClientRect().y < 0;
      } else {
        this.showSurveySelectionsInAppBar = false;
      }
    },
    handleSearch(searchPhrase) {
      this.searchPhrase = searchPhrase;
    },
    goToSurveys() {
      this.$router.push({name: 'tenant_surveys'});
    },
    setSelectedSurveys() {
      if (!this.selectedPersonnelSurvey && this.personnelSurveys.length > 0) {
        const personnelSurvey = this.personnelSurveys[0];
        if (personnelSurvey) {
          this.$store.commit('setSelectedPersonnelSurvey', personnelSurvey.key);
        }
      }
      if (!this.selectedManagementSurvey && this.managementSurveys.length > 0) {
        const managementSurvey = this.managementSurveys[0];
        if (managementSurvey) {
          this.$store.commit('setSelectedManagementSurvey', managementSurvey.key);
        }
      }
    },
    async reloadReports() {
      if (!this.loading) {
        this.subtleLoading = true;
      }
      try {
        if (this.$isPaidTenant && this.selectedManagementSurveyShares.length === 0 && this.selectedPersonnelSurveyShares.length === 0) {
          const surveys = [this.selectedPersonnelSurvey || false, this.selectedManagementSurvey || false].filter(Boolean);
          this.reports = await surveyApi.getSurveysReports(surveys);
        } else {
          let surveyShareKeys = []
          if (this.$isPaidTenant && (this.selectedManagementSurveyShares.length === 0 || this.selectedPersonnelSurveyShares.length === 0)) { // Either one of survey shares are empty, get every surveyshare (only if paid customer)
            surveyShareKeys = [
              ...(this.selectedManagementSurveyShares.length === 0 ? this.managementSurveyShares.map(s => s.key) : this.selectedManagementSurveyShares),
              ...(this.selectedPersonnelSurveyShares.length === 0 ? this.personnelSurveyShares.map(s => s.key) : this.selectedPersonnelSurveyShares)
            ].filter(Boolean);
          } else {
            surveyShareKeys = [...this.selectedManagementSurveyShares, ...this.selectedPersonnelSurveyShares].filter(Boolean);
          }

          this.reports = await surveyApi.getSurveysReportsBySurveyShareKeys(surveyShareKeys);
        }


      } catch (e) {
        this.$handleApiError(e, this.$t('errors.error_loading_reports'));
      }
      this.subtleLoading = false;
    },
    async reloadSurveys() {
      if (this.surveys.length > 0) {
        this.subtleLoading = true;
        this.loading = false;
      } else {
        this.loading = true;
        this.subtleLoading = false;
      }
      try {
        let surveys = await surveyApi.getSurveys();

        this.surveys = surveys.sort((a, b) => {
          return new Date(b.end) - new Date(a.end);
        });

        if (!this.selectedPersonnelSurvey && !this.selectedManagementSurvey) {
          await this.setSelectedSurveys();
        }
        await this.reloadReports();
      } catch (e) {
        this.$handleApiError(e, this.$t('errors.error_loading_surveys'));
      }

      this.loading = false;
      this.subtleLoading = false;

      if (this.$router.currentRoute.params.inputMap) {
        setTimeout(() => {
          this.scrollToInputMap();
        }, 500);
      }
    },
    scrollToInputMap() {
      if (this.$refs['inputMap']) {
        this.$vuetify.goTo(this.$refs['inputMap'], {duration: 500, offset: 0, easing: 'easeInOutCubic'});
      }
    },
  },
  mounted() {
    this.reloadSurveys();
  },
};
</script>

